// app/javascript/controllers/modal_controller.js
import { Controller } from "@hotwired/stimulus"
import { stimulus } from "../init"

export default class Modal extends Controller {
  static targets = ["modal", "backdrop", "content"]
  static classes = ["open", "loading"]
  static values = { 
    preventEscape: { type: Boolean, default: false },
    preventBackdropClick: { type: Boolean, default: false },
    autoOpen: { type: Boolean, default: false },
    size: { type: String, default: "normal" },
    closable: { type: Boolean, default: true },
    url: String,
    method: { type: String, default: "GET" },
    loading: { type: Boolean, default: false }
  }

  connect() {
    this.boundHandleEscape = this.handleEscape.bind(this)
    this.boundHandleFocusTrap = this.handleFocusTrap.bind(this)
    
    if (this.autoOpenValue) {
      this.open()
    }

    // Set up modal size classes
    this.setupModalSize()
  }

  disconnect() {
    this.cleanup()
  }

  // Main modal actions
  async open(event = null) {
    if (event) event.preventDefault()
    
    // Load content if URL is provided
    if (this.hasUrlValue && !this.contentLoaded) {
      await this.loadContent()
    }

    console.log("event");

    this.showModal()
    this.setupAccessibility()
    this.dispatch("opened", { detail: { modal: this.element } })
  }

  close(event = null) {
    if (event) event.preventDefault()
    
    this.hideModal()
    this.cleanup()
    this.dispatch("closed", { detail: { modal: this.element } })
  }

  toggle(event = null) {
    if (this.isOpen()) {
      this.close(event)
    } else {
      this.open(event)
    }
  }

  // Content loading
  async loadContent() {
    if (this.loadingValue) return
    
    this.loadingValue = true
    this.showLoading()

    try {
      const response = await fetch(this.urlValue, {
        method: this.methodValue,
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const html = await response.text()
      this.contentTarget.innerHTML = html
      this.contentLoaded = true
      
      this.dispatch("contentLoaded", { 
        detail: { modal: this.element, content: html } 
      })
    } catch (error) {
      this.handleError(error)
    } finally {
      this.loadingValue = false
      this.hideLoading()
    }
  }

  // Event handlers
  backdropClick(event) {
    if (this.preventBackdropClickValue) return
    if (event.target === this.backdropTarget || event.target === this.modalTarget) {
      this.close()
    }
  }

  handleEscape(event) {
    if (event.key === "Escape" && !this.preventEscapeValue) {
      this.close()
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // Form handling
  handleFormSubmit(event) {
    const form = event.target
    if (form.dataset.closeOnSuccess === "true") {
      // Listen for successful form submission
      form.addEventListener("turbo:submit-end", (e) => {
        if (e.detail.success) {
          this.close()
        }
      }, { once: true })
    }
  }

  // Turbo integration
  submitEnd(event) {
    if (event.detail.success && event.target.dataset.closeOnSuccess !== "false") {
      // Small delay to allow for flash messages or redirects
      setTimeout(() => this.close(), 100)
    }
  }

  frameLoaded() {
    // Handle turbo frame loaded events
    this.dispatch("frameLoaded", { detail: { modal: this.element } })
  }

  // Private methods
  showModal() {
    console.log(this.modalTarget)
    this.modalTarget.classList.add("modal-open")
    document.body.classList.add("modal-ope", "overflow-hidden")
    
    if (this.hasOpenClass) {
      this.modalTarget.classList.add(...this.openClasses)
    }
  }

  hideModal() {
    this.modalTarget.classList.remove("modal-open")
    document.body.classList.remove("modal-ope", "overflow-hidden")
    
    if (this.hasOpenClass) {
      this.modalTarget.classList.remove(...this.openClasses)
    }
  }

  showLoading() {
    if (this.hasLoadingClass) {
      this.modalTarget.classList.add(...this.loadingClasses)
    }
    
    if (this.hasContentTarget) {
      this.contentTarget.innerHTML = this.loadingHTML()
    }
  }

  hideLoading() {
    if (this.hasLoadingClass) {
      this.modalTarget.classList.remove(...this.loadingClasses)
    }
  }

  setupModalSize() {
    const sizeClasses = {
      small: "max-w-sm",
      normal: "max-w-lg", 
      large: "max-w-4xl",
      xlarge: "max-w-6xl",
      full: "max-w-none w-full h-full"
    }

    const modalBox = this.modalTarget.querySelector('.modal-box')
    if (modalBox && sizeClasses[this.sizeValue]) {
      modalBox.classList.add(sizeClasses[this.sizeValue])
    }
  }

  setupAccessibility() {
    // Focus management
    this.previousActiveElement = document.activeElement
    
    const focusableElements = this.modalTarget.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    
    this.focusableElements = Array.from(focusableElements)
    
    if (this.focusableElements.length > 0) {
      this.focusableElements[0].focus()
    }

    // Add event listeners
    document.addEventListener("keydown", this.boundHandleEscape)
    document.addEventListener("keydown", this.boundHandleFocusTrap)
    
    // ARIA attributes
    this.modalTarget.setAttribute("aria-modal", "true")
    this.modalTarget.setAttribute("role", "dialog")
  }

  handleFocusTrap(event) {
    if (!this.isOpen()) return
    
    if (event.key === "Tab") {
      const firstElement = this.focusableElements[0]
      const lastElement = this.focusableElements[this.focusableElements.length - 1]

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault()
          firstElement.focus()
        }
      }
    }
  }

  cleanup() {
    // Remove event listeners
    document.removeEventListener("keydown", this.boundHandleEscape)
    document.removeEventListener("keydown", this.boundHandleFocusTrap)
    
    // Restore focus
    if (this.previousActiveElement) {
      this.previousActiveElement.focus()
    }

    // Clear ARIA attributes
    this.modalTarget.removeAttribute("aria-modal")
    this.modalTarget.removeAttribute("role")
  }

  isOpen() {
    return this.modalTarget.classList.contains("modal-open")
  }

  handleError(error) {
    console.error("Modal error:", error)
    
    if (this.hasContentTarget) {
      this.contentTarget.innerHTML = this.errorHTML(error.message)
    }
    
    this.dispatch("error", { 
      detail: { modal: this.element, error: error.message } 
    })
  }

  loadingHTML() {
    return `
      <div class="flex items-center justify-center p-8">
        <div class="loading loading-spinner loading-lg"></div>
        <span class="ml-2">Loading...</span>
      </div>
    `
  }

  errorHTML(message) {
    return `
      <div class="alert alert-error">
        <div>
          <h3 class="font-bold">Error Loading Content</h3>
          <div class="text-xs">${message}</div>
        </div>
      </div>
    `
  }
}

stimulus.register('modal', Modal)